import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { currency, fmtDate, QUOTE_TYPES } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { QuotePdfActions } from "../components/QuotePdfActions";

export default function Quotes() {
  const { user } = useAuth();
  const admin = ["super_admin", "admin", "admin_manager"].includes(user?.role);
  const [recycle, setRecycle] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => api.get(recycle ? "/quotes/recycle-bin" : "/quotes").then((r) => setQuotes(r.data)).catch((e) => toast.error(errMsg(e))), [recycle]);
  useEffect(() => { load(); api.get("/leads").then((r) => setLeads(r.data)); }, [load]);
  const remove = async (q) => {
    if (!window.confirm(`Move ${q.quote_no}${q.signed ? " (signed)" : ""} to the recycle bin? Its public link will stop working. Payment and invoice records are kept.`)) return;
    try { await api.delete(`/quotes/${q.id}`); toast.success("Quote moved to recycle bin"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async (q) => {
    try { await api.post(`/quotes/${q.id}/restore`); toast.success("Quote restored"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const leadName = (id) => { const l = leads.find((x) => x.id === id); return l ? `${l.first_name} ${l.last_name}` : "-"; };
  const typeLabel = (t) => QUOTE_TYPES.find((x) => x.key === t)?.label || t;
  const copyLink = (tok) => { navigator.clipboard.writeText(`${window.location.origin}/quote/${tok}`); toast.success("Public link copied"); };

  const statusColor = { draft: "bg-muted", sent: "bg-secondary/10 text-secondary", viewed: "bg-primary/10 text-primary", accepted: "bg-green-100 text-green-700" };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-3xl font-head font-extrabold tracking-tight">{recycle ? "Quote recycle bin" : "Quotes"}</h1>
          <p className="text-muted-foreground">{quotes.length} quotes</p></div>
        {admin && <button data-testid="quotes-recycle-toggle" onClick={() => setRecycle(!recycle)} className="px-3 py-2 rounded-md border border-border text-sm flex items-center gap-2"><Icons.Trash2 size={16} />{recycle ? "Active quotes" : "Recycle bin"}</button>}
        <button data-testid="new-quote-btn" onClick={() => navigate("/app/quotes/new")}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">
          <Icons.Plus size={18} /> New Quote
        </button>
      </div>

      <div className="bg-card border border-border rounded-md shadow-sm overflow-x-auto">
        <table className="crm-compact-table w-full text-sm" data-testid="quotes-table">
          <thead><tr className="text-left label-caps border-b border-border">
            <th className="p-3">Quote No</th><th>Customer</th><th>Type</th><th>System Price</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {quotes.map((q) => (
              <tr key={q.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                <td className="p-3 font-semibold text-primary"><button disabled={recycle} data-testid={`open-quote-${q.id}`} onClick={() => navigate(`/app/quotes/${q.id}`)} className="hover:underline">{q.quote_no}</button>
                  <div className="xl:hidden text-xs text-muted-foreground font-normal mt-1 break-words">{leadName(q.lead_id)} · {currency(q.out_of_pocket)} · {q.status}</div></td>
                <td>{leadName(q.lead_id)}</td>
                <td className="text-xs">{typeLabel(q.quote_type)}</td>
                <td>{currency(q.out_of_pocket)}<span className="text-xs text-muted-foreground"> oop</span></td>
                <td><span className={`text-xs font-semibold px-2 py-0.5 rounded capitalize ${statusColor[q.status] || "bg-muted"}`}>{q.status}{q.signed && " ✓"}</span></td>
                <td className="text-xs text-muted-foreground">{fmtDate(q.created_at)}</td>
                <td className="p-3">
                  {recycle ? <button data-testid={`restore-quote-${q.id}`} onClick={() => restore(q)} className="flex items-center gap-1 text-primary text-sm font-semibold"><Icons.Undo2 size={16} />Restore</button> : <QuotePdfActions quote={q} id={`quote-list-actions-${q.id}`} compact label={`Actions for ${q.quote_no}`} items={[
                    { id: `edit-quote-${q.id}`, icon: "Pencil", label: "Open quote editor", onSelect: () => navigate(`/app/quotes/${q.id}`) },
                    { id: `online-quote-${q.id}`, icon: "Eye", label: "Preview Quote", onSelect: () => window.open(`/quote/${q.public_token}?preview=1`, "_blank", "noopener,noreferrer") },
                    { id: `copy-link-${q.id}`, icon: "Link2", label: "Copy link", onSelect: () => copyLink(q.public_token) },
                    ...(admin ? [{ id: `delete-quote-${q.id}`, icon: "Trash2", label: "Move to recycle bin", onSelect: () => remove(q) }] : []),
                  ]} />}
                </td>
              </tr>
            ))}
            {quotes.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No quotes yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
